import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";

import { api } from "../../convex/_generated/api";
import { Authenticated, AuthLoading, Unauthenticated } from "@/components/auth-gate";
import { RedirectToLogin } from "@/components/shell/redirect-to-login";
import { Splash } from "@/components/shell/splash";
import { Wordmark } from "@/components/shell/wordmark";
import { StatusChip } from "@/components/studio-console/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster } from "@/components/ui/sonner";
import { authClient } from "@/lib/auth-client";
import { said } from "@/lib/errors";
import { organizationSlug } from "@/lib/organizations";

export const Route = createFileRoute("/studio_/setup")({
  component: StudioSetupPage,
});

function StudioSetupPage() {
  const { t: tc } = useTranslation();
  return (
    <>
      <AuthLoading>
        <Splash subtitle={tc("status.loading")} />
      </AuthLoading>
      <Unauthenticated>
        <RedirectToLogin />
      </Unauthenticated>
      <Authenticated>
        <Setup />
      </Authenticated>
      <Toaster />
    </>
  );
}

const em = { em: <span className="italic" /> };

/** Where a design studio starts: set it up, then wait for Ours to look at it. */
function Setup() {
  const { t: tc } = useTranslation();
  const companies = useQuery(api.organizations.mine);
  const me = useQuery(api.auth.getCurrentUser);
  if (companies === undefined || me === undefined) return <Splash subtitle={tc("status.loading")} />;
  const studio = companies.find((c) => c.kind === "studio") ?? null;
  return (
    <main className="mx-auto grid min-h-svh max-w-md content-center gap-6 px-6 py-10">
      <Wordmark size="md" />
      {studio ? <Waiting studio={studio} email={me?.email ?? ""} /> : <Form />}
    </main>
  );
}

function Form() {
  const { t } = useTranslation("studio");
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const { error } = await authClient.organization.create({
        name: name.trim(),
        slug: organizationSlug(name),
        kind: "studio",
        website: website.trim(),
        keepCurrentActiveOrganization: true,
      });
      if (error) throw new Error(error.message || t("setup.failed"));
      // The page follows on its own: organizations.mine now has the studio.
    } catch (error) {
      toast.error(said(error, t("setup.failed")));
      setBusy(false);
    }
  }

  return (
    <>
      <div className="grid gap-2">
        <h1 className="font-display text-3xl leading-tight text-ink">
          <Trans t={t} i18nKey="setup.title" components={em} />
        </h1>
        <p className="text-sm text-ink-2">{t("setup.body")}</p>
      </div>
      <form onSubmit={(event) => void submit(event)} className="grid gap-3.5">
        <div className="grid gap-1.5">
          <Label htmlFor="studio-name">{t("setup.name")}</Label>
          <Input
            id="studio-name"
            value={name}
            maxLength={80}
            required
            placeholder={t("setup.namePlaceholder")}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="studio-website">{t("setup.website")}</Label>
          <Input
            id="studio-website"
            value={website}
            required
            placeholder={t("setup.websitePlaceholder")}
            onChange={(event) => setWebsite(event.target.value)}
          />
        </div>
        <Button type="submit" disabled={busy || !name.trim() || !website.trim()}>
          {t("setup.submit")}
        </Button>
      </form>
    </>
  );
}

type Studio = NonNullable<ReturnType<typeof useQuery<typeof api.organizations.mine>>>[number];

function Waiting({ studio, email }: { studio: Studio; email: string }) {
  const { t } = useTranslation("studio");
  const switchTo = useMutation(api.workspaces.switchTo);
  const navigate = useNavigate();
  const runs = studio.role.split(",").some((r) => r === "owner" || r === "admin");

  async function openStudio() {
    if (studio.workspaceId) await switchTo({ workspaceId: studio.workspaceId });
    await navigate({ to: "/" });
  }

  if (studio.status === "approved") {
    return (
      <div className="grid gap-4">
        <div>
          <StatusChip status="approved" />
        </div>
        <h1 className="font-display text-3xl leading-tight text-ink">
          <Trans t={t} i18nKey="setup.approvedTitle" values={{ name: studio.name }} components={em} />
        </h1>
        <p className="text-sm text-ink-2">{t("setup.approvedBody")}</p>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => void openStudio()}>{t("setup.openStudio")}</Button>
          {runs ? (
            <Button variant="outline" asChild>
              <Link to="/studio/console">{t("setup.openConsole")}</Link>
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  if (studio.status === "rejected") {
    return (
      <div className="grid gap-4">
        <div>
          <StatusChip status="rejected" />
        </div>
        <h1 className="font-display text-3xl leading-tight text-ink">
          {t("setup.rejectedTitle", { name: studio.name })}
        </h1>
        {studio.rejectedBecause ? (
          <p className="text-sm text-ink-2">{t("setup.rejectedBody", { reason: studio.rejectedBecause })}</p>
        ) : null}
        <Button variant="outline" className="justify-self-start" asChild>
          <Link to="/settings/help">
            {t("setup.writeToUs")}
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="grid gap-4">
      <div>
        <StatusChip status="pending" />
      </div>
      <h1 className="font-display text-3xl leading-tight text-ink">
        <Trans t={t} i18nKey="setup.pendingTitle" values={{ name: studio.name }} components={em} />
      </h1>
      <p className="text-sm text-ink-2">{t("setup.pendingBody", { email })}</p>
      <p className="text-sm text-ink-2">{t("setup.pendingMeanwhile")}</p>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" asChild>
          <Link to="/">{t("setup.tryHome")}</Link>
        </Button>
      </div>
    </div>
  );
}
